import React, { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';

import OlcButton from '~/components/General/OlcButton';
import OlcDataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';

import DashboardHeader from '../MailTracking/DashboardHeader';
import { DASHBOARD_PAGE_SIZE } from '../../constants';
import useDashboardPagination from '../../hooks/useDashboardPagination';
import { formatNumber, formatPercent, getLastRefreshedLabel, getStatusTone } from '../../utils';

const getChannelsBackground = (channels) => {
  let cursor = 0;
  const stops = channels.map((channel) => {
    const stop = `${channel.color} ${cursor}% ${cursor + channel.percent}%`;
    cursor += channel.percent;
    return stop;
  });

  return stops.length ? `conic-gradient(${stops.join(', ')})` : 'none';
};

const getDateSortValue = (value) => {
  if (!value) return null;

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

// Badge tone per engagement channel; unknown tracking types keep the previous QR (warning) look.
const TRACKING_TYPE_TONES = {
  phone: 'info',
  qr: 'warning',
  purl: 'scheduled',
};

const RESPONSE_CHANNEL_TONES = {
  phone: 'info',
  qr: 'warning',
  purl: 'scheduled',
};

const getTrackingTypeTone = (type) =>
  TRACKING_TYPE_TONES[String(type || '').toLowerCase()] || 'warning';

const getResponseChannelTone = (row) =>
  RESPONSE_CHANNEL_TONES[row.sourceType] || getStatusTone(row.channel);

const getStringSortValue = (value) => String(value || '').toLowerCase();
const isEmptySortValue = (value) => value === null || value === undefined || value === '';

const getTrackingHistoryPath = (row) => {
  const trackingAssetId = row?.trackingAssetId;
  if (!trackingAssetId) return null;

  const type = String(row.type || '').toLowerCase();
  if (type.includes('phone')) return `/phone-tracking/${trackingAssetId}/call`;
  if (type.includes('qr')) return `/engagement/qr-code-history/${trackingAssetId}`;
  if (type.includes('purl')) return `/engagement/purl-history/${trackingAssetId}`;

  return null;
};

const sortRows = (rows = [], sortModel = [], valueGetters = {}) => {
  const [sort] = sortModel;
  if (!sort?.field || !sort?.sort) return rows;

  const direction = sort.sort === 'asc' ? 1 : -1;
  const getSortValue = valueGetters[sort.field] || ((row) => row[sort.field]);

  return [...rows].sort((leftRow, rightRow) => {
    const leftValue = getSortValue(leftRow);
    const rightValue = getSortValue(rightRow);
    const leftEmpty = isEmptySortValue(leftValue);
    const rightEmpty = isEmptySortValue(rightValue);

    if (leftEmpty || rightEmpty) {
      if (leftEmpty && rightEmpty) return 0;
      return leftEmpty ? 1 : -1;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    return String(leftValue ?? '').localeCompare(String(rightValue ?? '')) * direction;
  });
};

const EngagementTab = ({ engagement, isLoading, isRefreshing, dateRange, onRefresh }) => {
  const navigate = useNavigate();
  const [trackingSortModel, setTrackingSortModel] = useState([
    { field: 'lastTracked', sort: 'desc' },
  ]);
  const [responsesSortModel, setResponsesSortModel] = useState([{ field: 'when', sort: 'desc' }]);
  const channelsBackground = useMemo(
    () => getChannelsBackground(engagement.responseChannels),
    [engagement.responseChannels]
  );
  const sortedTrackingAssets = useMemo(
    () =>
      sortRows(engagement.trackingAssets, trackingSortModel, {
        asset: (row) => getStringSortValue(row.asset),
        type: (row) => getStringSortValue(row.type),
        lastTracked: (row) => getDateSortValue(row.lastTrackedAt),
        lastMailed: (row) => getDateSortValue(row.lastMailedAt),
      }),
    [engagement.trackingAssets, trackingSortModel]
  );
  const sortedRecentResponses = useMemo(
    () =>
      sortRows(engagement.recentResponses, responsesSortModel, {
        contact: (row) => getStringSortValue(row.contact),
        order: (row) => getStringSortValue(row.order),
        channel: (row) => getStringSortValue(row.channel),
        when: (row) => getDateSortValue(row.whenAt),
      }),
    [engagement.recentResponses, responsesSortModel]
  );
  const usedProductPerformance = useMemo(
    () => engagement.performanceByProduct.filter((product) => product.trackedDelivered > 0),
    [engagement.performanceByProduct]
  );
  const trackingPagination = useDashboardPagination(sortedTrackingAssets, DASHBOARD_PAGE_SIZE);
  const responsesPagination = useDashboardPagination(sortedRecentResponses, DASHBOARD_PAGE_SIZE);
  const phoneChannel = engagement.responseChannels.find((channel) => channel.id === 'phone');
  const qrChannel = engagement.responseChannels.find((channel) => channel.id === 'qr');
  const purlChannel = engagement.responseChannels.find((channel) => channel.id === 'purl');
  const areCardsDisabled = engagement.isDashboardCardsDisabled === true;
  const hasResponses = Number(engagement.totalResponses) > 0;
  const hasResponseBreakdown =
    hasResponses
      && (engagement.responseChannels.length > 0 || usedProductPerformance.length > 0);
  const phoneEventPercent = hasResponses ? phoneChannel?.percent || 0 : 0;
  const qrEventPercent = hasResponses ? qrChannel?.percent || 0 : 0;
  const purlEventPercent = hasResponses ? purlChannel?.percent || 0 : 0;
  const handleTrackingRowClick = ({ row }) => {
    if (areCardsDisabled) return;

    const path = getTrackingHistoryPath(row);
    if (path) navigate(path);
  };

  const trackingColumns = useMemo(
    () => [
      {
        field: 'asset',
        headerName: 'Tracking asset',
        minWidth: 220,
        flex: 1.4,
        sortable: true,
        renderCell: ({ row }) => (
          <div className="dashboard-primary-cell">
            <strong>{row.asset}</strong>
            <small>{row.campaign}</small>
          </div>
        ),
        compactValueGetter: (row) => `${row.asset} ${row.campaign}`,
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 110,
        flex: 0.6,
        sortable: true,
        renderCell: ({ row }) => (
          <span className={`status-badge ${getTrackingTypeTone(row.type)}`}>
            {row.type}
          </span>
        ),
      },
      {
        field: 'mailDelivered',
        headerName: 'Mail delivered',
        align: 'right',
        headerAlign: 'right',
        minWidth: 140,
        flex: 0.8,
        sortable: true,
        compactValueGetter: (row) => formatNumber(row.mailDelivered),
        renderCell: ({ row }) => formatNumber(row.mailDelivered),
      },
      {
        field: 'responses',
        headerName: 'Responses',
        align: 'right',
        headerAlign: 'right',
        minWidth: 120,
        flex: 0.7,
        sortable: true,
        compactValueGetter: (row) => formatNumber(row.responses),
        renderCell: ({ row }) => formatNumber(row.responses),
      },
      {
        field: 'responseRate',
        headerName: 'Response rate',
        align: 'right',
        headerAlign: 'right',
        minWidth: 130,
        flex: 0.7,
        sortable: true,
        compactValueGetter: (row) => formatPercent(row.responseRate),
        renderCell: ({ row }) => formatPercent(row.responseRate),
      },
      {
        field: 'lastTracked',
        headerName: 'Last tracked',
        align: 'right',
        headerAlign: 'right',
        minWidth: 140,
        flex: 0.8,
        sortable: true,
        compactValueGetter: (row) => row.lastTracked,
        valueGetter: ({ row }) => getDateSortValue(row.lastTrackedAt),
        renderCell: ({ row }) => row.lastTracked,
      },
      {
        field: 'lastMailed',
        headerName: 'Last mailed',
        align: 'right',
        headerAlign: 'right',
        minWidth: 130,
        flex: 0.8,
        sortable: true,
        compactValueGetter: (row) => row.lastMailed,
        valueGetter: ({ row }) => getDateSortValue(row.lastMailedAt),
        renderCell: ({ row }) => row.lastMailed,
      },
    ],
    []
  );

  const responseColumns = useMemo(
    () => [
      {
        field: 'contact',
        headerName: 'Contact',
        minWidth: 180,
        flex: 1,
        sortable: true,
        renderCell: ({ row }) => <strong className="dashboard-plain-strong">{row.contact}</strong>,
      },
      {
        field: 'order',
        headerName: 'Order',
        minWidth: 230,
        flex: 1.2,
        sortable: true,
      },
      {
        field: 'channel',
        headerName: 'Channel',
        minWidth: 130,
        flex: 0.7,
        sortable: true,
        renderCell: ({ row }) => (
          <span className={`status-badge ${getResponseChannelTone(row)}`}>{row.channel}</span>
        ),
      },
      {
        field: 'when',
        headerName: 'When',
        minWidth: 120,
        flex: 0.7,
        sortable: true,
        compactValueGetter: (row) => row.when,
        valueGetter: ({ row }) => getDateSortValue(row.whenAt),
        renderCell: ({ row }) => row.when,
      },
    ],
    []
  );

  return (
    <>
      <DashboardHeader
        title="Response Overview"
        subtitle="See who responded to your mail and how."
        lastRefreshed={getLastRefreshedLabel(engagement.lastRefreshedAt)}
        isRefreshing={isRefreshing}
        dateRange={dateRange}
        onRefresh={onRefresh}
      />

      {!hasResponses && !areCardsDisabled ? (
        <div className="dashboard-info-callout" role="status">
          <InfoOutlinedIcon />
          <p>
            <strong>Tracking is active on your mail - no responses yet.</strong>
            {' '}
            Calls, scans and PURL visits will show up here as recipients respond.
          </p>
        </div>
      ) : null}

      <p className="dashboard-scope-note">
        Based on <strong>{formatNumber(engagement.trackedDelivered)}</strong> delivered pieces with
        tracking enabled. Untracked mail is excluded from response rate.
      </p>

      <section
        className={`dashboard-metric-grid dashboard-metric-grid--five ${areCardsDisabled ? 'is-disabled' : ''}`}
        aria-label="Engagement overview"
        aria-disabled={areCardsDisabled || undefined}
      >
        <article className="dashboard-metric-card">
          <span>Unique responders</span>
          <strong>{formatNumber(engagement.uniqueResponders)}</strong>
          <small>{formatNumber(engagement.totalResponses)} total events</small>
        </article>
        <article className="dashboard-metric-card">
          <span>Response rate</span>
          <strong>{formatPercent(engagement.responseRate)}</strong>
          <small>share of tracked mail that responded</small>
        </article>
        <article className="dashboard-metric-card">
          <span>Phone calls</span>
          <strong>{formatNumber(engagement.phoneCalls)}</strong>
          <small>
            {formatPercent(phoneEventPercent)} of events
          </small>
        </article>
        <article className="dashboard-metric-card">
          <span>QR scans</span>
          <strong>{formatNumber(engagement.qrScans)}</strong>
          <small>
            {formatPercent(qrEventPercent)} of events
          </small>
        </article>
        <article className="dashboard-metric-card">
          <span>PURL visits</span>
          <strong>{formatNumber(engagement.purlVisits)}</strong>
          <small>
            {formatPercent(purlEventPercent)} of events
          </small>
        </article>
      </section>

      {hasResponseBreakdown ? (
        <section
          className={`engagement-insight-grid ${areCardsDisabled ? 'is-disabled' : ''}`}
          aria-label="Response insights"
          aria-disabled={areCardsDisabled || undefined}
        >
          <article className="dashboard-card response-channels-card">
            <h2>Response channels</h2>
            <div className="response-channel-content">
              <div className="response-donut" style={{ background: channelsBackground }}>
                <span />
              </div>
              <div className="product-legend">
                {engagement.responseChannels.map((channel) => (
                  <div key={channel.id}>
                    <i style={{ background: channel.color }} />
                    <span>{channel.label}</span>
                    <strong>{formatPercent(channel.percent)}</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>
          <article className="dashboard-card product-response-card">
            <h2>Response rate by product</h2>
            <div className="product-response-list">
              {usedProductPerformance.map((product) => (
                <div key={product.id}>
                  <div>
                    <span>{product.product}</span>
                    <strong>{formatPercent(product.responseRate)}</strong>
                  </div>
                  <span className="product-response-track">
                    <i style={{ width: `${product.responseRate}%` }} />
                  </span>
                </div>
              ))}
            </div>
          </article>
        </section>
      ) : (
        <section className="dashboard-card engagement-empty-breakdown" aria-label="Response insights">
          <h2>No responses to break down yet</h2>
          <p>
            Channels and product response rates appear once
            <br />
            responses come in.
          </p>
        </section>
      )}

      <section className={`dashboard-table-section ${areCardsDisabled ? 'is-disabled' : ''}`}>
        <div className="section-heading">
          <div>
            <div className="heading-row">
              <h2>Tracking performance</h2>
              <span className="time-pill">All time</span>
            </div>
            <p>Mail delivered and responses for each tracking phone number, QR code and PURL.</p>
          </div>
          <div className="section-actions">
            <OlcButton
              type="button"
              variant="secondary"
              size="small"
              startIcon={<PhoneOutlinedIcon />}
              onClick={() => navigate('/phone-tracking')}
              disabled={areCardsDisabled}
            >
              Manage tracking
            </OlcButton>
          </div>
        </div>
        <div className="dashboard-grid-shell">
          <OlcDataGrid
            rows={trackingPagination.visibleRows}
            columns={trackingColumns}
            loading={isLoading}
            hideFooter
            rowSelection={false}
            disableRowSelectionOnClick
            disableColumnMenu
            rowHeight={61}
            columnHeaderHeight={48}
            getRowId={(row) => row.id}
            // Sorting is applied before local pagination so every fetched row participates.
            sortingMode="server"
            sortModel={trackingSortModel}
            onRowClick={handleTrackingRowClick}
            onSortModelChange={(nextSortModel) => {
              setTrackingSortModel(nextSortModel);
              trackingPagination.resetPage();
            }}
            slots={{
              noRowsOverlay: () => (
                <div className="dashboard-grid-empty">
                  No tracking performance yet.
                </div>
              ),
            }}
          />
          <OlcTablePagination
            count={trackingPagination.pageCount}
            page={trackingPagination.page}
            pageSize={trackingPagination.pageSize}
            onPageChange={(_, nextPage) => trackingPagination.setPage(nextPage)}
            from={trackingPagination.from}
            to={trackingPagination.to}
            total={trackingPagination.total}
            loading={isLoading}
            showPageSize={false}
          />
        </div>
      </section>

      <section className={`dashboard-table-section ${areCardsDisabled ? 'is-disabled' : ''}`}>
        <div className="section-heading">
          <div>
            <div className="heading-row">
              <h2>Recent responses</h2>
              <span className="time-pill">Last 7 days</span>
            </div>
            <p>Calls, scans and visits tied back to your mail, newest first.</p>
          </div>
          <div className="section-actions">
            <OlcButton
              type="button"
              variant="secondary"
              size="small"
              startIcon={<QrCode2OutlinedIcon />}
              onClick={() => navigate('/engagement/qr-codes')}
              disabled={areCardsDisabled}
            >
              Manage QR codes
            </OlcButton>
            <OlcButton
              type="button"
              variant="secondary"
              size="small"
              startIcon={<InsertLinkOutlinedIcon />}
              onClick={() => navigate('/engagement/purls')}
              disabled={areCardsDisabled}
            >
              Manage PURLs
            </OlcButton>
          </div>
        </div>
        <div className="dashboard-grid-shell">
          <OlcDataGrid
            rows={responsesPagination.visibleRows}
            columns={responseColumns}
            loading={isLoading}
            hideFooter
            rowSelection={false}
            disableRowSelectionOnClick
            disableColumnMenu
            rowHeight={61}
            columnHeaderHeight={48}
            getRowId={(row) => row.id}
            // Sorting is applied before local pagination so every fetched row participates.
            sortingMode="server"
            sortModel={responsesSortModel}
            onSortModelChange={(nextSortModel) => {
              setResponsesSortModel(nextSortModel);
              responsesPagination.resetPage();
            }}
            slots={{
              noRowsOverlay: () => (
                <div className="dashboard-grid-empty">
                  No responses in the last 7 days.
                </div>
              ),
            }}
          />
          <OlcTablePagination
            count={responsesPagination.pageCount}
            page={responsesPagination.page}
            pageSize={responsesPagination.pageSize}
            onPageChange={(_, nextPage) => responsesPagination.setPage(nextPage)}
            from={responsesPagination.from}
            to={responsesPagination.to}
            total={responsesPagination.total}
            loading={isLoading}
            showPageSize={false}
          />
        </div>
      </section>
    </>
  );
};

export default EngagementTab;
